import { Tooltip, useTestTag } from '@myosh/odin-components';
import React, { useMemo } from 'react';
import type { RecordResult } from '../../../@types/records';
import { useRiskRatingMatrixConfigQuery } from '../../../redux/services/risk-matrix';
import type { RiskRatingGroup } from '../../../utils/risk-rating-utils';
import {
  extractRiskRatingGroups,
  formatSegmentText,
  getRiskRatingCellAttributes,
} from '../../../utils/risk-rating-utils';

interface RiskRatingDisplayProps {
  records: RecordResult[];
  linkedFieldId: number;
  riskMatrixId: number | undefined;
  displayRecordListColour?: boolean;
}

type DisplaySegment = {
  text: string;
  style?: React.CSSProperties;
};

const RiskRatingDisplay = ({
  records,
  linkedFieldId,
  riskMatrixId,
  displayRecordListColour,
}: RiskRatingDisplayProps) => {
  const tagCreator = useTestTag('recordlink-risk-rating-display');

  if (!linkedFieldId) {
    return null;
  }

  const { data: riskMatrixData } = useRiskRatingMatrixConfigQuery(riskMatrixId, {
    skip: !riskMatrixId,
  });

  const riskRatingGroups = useMemo<RiskRatingGroup[]>(
    () => extractRiskRatingGroups(records, linkedFieldId),
    [records, linkedFieldId]
  );
  const displaySegments = useMemo(() => {
    return riskRatingGroups.map((group) => {
      const cellAttributes =
        riskMatrixData && displayRecordListColour !== false
          ? getRiskRatingCellAttributes(riskMatrixData, group.riskLevelId)
          : { color: 'inherit', backgroundColor: 'transparent' };

      const displayText = formatSegmentText(group);

      return {
        text: displayText,
        style: cellAttributes,
      };
    });
  }, [riskRatingGroups, riskMatrixData, displayRecordListColour]);

  if (displaySegments.length === 0) {
    return null;
  }

  const rootClass = 'odin-flex odin-items-center odin-h-full odin-w-full odin-min-w-0';
  const overflowWrapperClass = `${rootClass} odin-overflow-hidden odin-gap-2`;
  const segmentContainerClass = 'odin-flex odin-flex-1 odin-min-w-0 odin-overflow-hidden';
  const tooltipClass = 'bg-mono-1';
  const tooltipWrapperClass = 'odin-truncate odin-min-w-0 odin-pl-2 odin-pr-2';

  return (
    <div ref={tagCreator('')} className={rootClass}>
      <div className={overflowWrapperClass}>
        {displaySegments.map((segment) => (
          <div key={segment.text} className={segmentContainerClass} style={segment.style}>
            <Tooltip
              description={segment.text}
              tooltipClassName={tooltipClass}
              tooltipZIndexCheck
              wrapperClassName={tooltipWrapperClass}
            >
              {segment.text}
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RiskRatingDisplay;
